import LogoSrc from '@/assets/large-logo.png';
import {
  APP_FULL_NAME,
  APP_SHORT_NAME,
  BASE_URL,
  CLUSTER,
  CLUSTER_URL,
} from '@/constants/app';
import { Paths } from '@/constants/paths';
import { useAdminLogin } from '@/hooks/use-auth';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { useProgramEvent } from '@/hooks/use-program';
import { useSnackbar } from '@/hooks/use-snackbar';
import { accessTokenAtom } from '@/stores/auth';
import { snackbarAtom } from '@/stores/layout';
import { programAtom, waitForLoggedAtom } from '@/stores/program';
import { UfcEventData } from '@/utils/program/idl/type';
import { getProgram } from '@/utils/program/init';
import { getVaultAddr } from '@/utils/program/pda';
import { SlopeWalletAdapter } from '@/utils/solana/slope';
import { TorusWalletAdapter } from '@/utils/solana/torus';
import { Trans } from '@lingui/react/macro';
import { Alert, Snackbar } from '@mui/material';
import { useNavigate } from '@remix-run/react';
import {
  Adapter,
  WalletAdapterNetwork,
  WalletError,
} from '@solana/wallet-adapter-base';
import { MathWalletAdapter } from '@solana/wallet-adapter-mathwallet';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import {
  ConnectionProvider,
  useAnchorWallet,
  useConnection,
  useWallet,
  WalletProvider,
} from '@solana/wallet-adapter-react';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { useAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import React, { lazy, Suspense, useCallback, useEffect, useMemo } from 'react';

const LazyConnectDialog = lazy(() =>
  import('@/components/modules/wallet/connect-dialog').then((r) => ({
    default: r.ConnectDialog,
  }))
);

export const WalletLayout: React.FCC = (props) => {
  const network = CLUSTER; // clusterApiUrl(NETWORK);
  const { enqueueSnackbar } = useSnackbar();

  const wallets = useMemo(() => {
    const wallets = [
      // new TrustWalletAdapter(),
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter({ network }),
      // new LedgerWalletAdapter(),
      new MathWalletAdapter(),
      // new TrezorWalletAdapter(),
      new TorusWalletAdapter({
        params: {
          network,
          showTorusButton: false,
          buildEnv:
            network === WalletAdapterNetwork.Mainnet ? 'production' : 'testing',
          enableLogging: true,
          useLocalStorage: true,
          whiteLabel: {
            logoDark: `${BASE_URL}${LogoSrc}`,
            logoLight: `${BASE_URL}${LogoSrc}`,
            theme: { isDark: true, colors: {} },
            name: APP_FULL_NAME,
            url: BASE_URL,
          },
        },
      }),
      new SlopeWalletAdapter(),
      // new CoinbaseWalletAdapter(),
    ];

    return wallets;
  }, []);

  const onError = useAtomCallback(
    useCallback(
      (get, set, error: WalletError, adapter?: Adapter) => {
        if (!adapter?.connected) {
          set(waitForLoggedAtom, { rejectAll: true });
        }

        let msg = error.message;
        if (msg) {
          enqueueSnackbar(msg, { variant: 'error' });
        }
      },
      [enqueueSnackbar]
    )
  );
  const mobile = useBreakpoint('mobile');
  useEffect(() => {
    if (!mobile) return;

    localStorage.removeItem(APP_SHORT_NAME);
    window.addEventListener('unload', () => {
      localStorage.removeItem(APP_SHORT_NAME);
    });
  }, [mobile]);

  return (
    <ConnectionProvider
      endpoint={CLUSTER_URL}
      config={{ commitment: 'confirmed' }}>
      <WalletProvider
        onError={onError}
        wallets={wallets}
        localStorageKey={APP_SHORT_NAME}
        autoConnect>
        <UFRProgram />
        <MySnackbar />
        {props.children}
        <Suspense>
          <LazyConnectDialog />
        </Suspense>
      </WalletProvider>
    </ConnectionProvider>
  );
};

const MySnackbar: React.FC = () => {
  const [snackbar, setSnackbar] = useAtom(snackbarAtom);

  const handleClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Snackbar
      anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
      autoHideDuration={6000}
      onClose={handleClose}
      open={snackbar.open}>
      <Alert
        onClose={handleClose}
        severity={snackbar.variant}
        variant="filled"
        sx={{ width: '100%' }}>
        {snackbar.msg}
      </Alert>
    </Snackbar>
  );
};

const UFRProgram: React.FC = (props) => {
  const anchorWallet = useAnchorWallet();
  const { connected } = useWallet();
  const [program, setProgram] = useAtom(programAtom);
  const navigate = useNavigate();
  const signAndLogin = useAdminLogin();
  const [jwt, logout] = useAtom(accessTokenAtom);

  const { connection } = useConnection();

  useEffect(() => {
    if (!connected) return;
    if (!anchorWallet) {
      setProgram(null);
      return;
    }

    setProgram(getProgram({ connection, wallet: anchorWallet }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorWallet, connected]);

  const { enqueueSnackbar } = useSnackbar();

  const onPayout = useCallback(
    ({
      data,
    }: {
      data: UfcEventData['instantBetReward'] | UfcEventData['paidTicket'];
    }) => {
      const me = anchorWallet?.publicKey;
      if (!me || !data.buyer.equals(me)) return;

      enqueueSnackbar(<Trans>Payout sent</Trans>, {
        variant: 'info',
      });
    },
    [anchorWallet, enqueueSnackbar]
  );

  useProgramEvent('instantBetReward', onPayout);
  useProgramEvent('paidTicket', onPayout);

  useEffect(() => {
    if (!program) return;

    (async () => {
      const vault = await program.account.vault.fetch(getVaultAddr(program));

      const signer = program.provider.publicKey;

      if (signer && !vault.operator.equals(signer)) {
        logout(null);
        return;
      }

      const ok = jwt ?? (await signAndLogin?.());
      if (ok === true) {
        navigate(Paths.AdminDashboard);
      }
    })();
  }, [program]);

  return null;
};
